import React from 'react';
import { Link } from 'react-router-dom';
import { useIsMobile, useIsTablet } from '../hooks/useMediaQuery';
import { BANNER_IMAGES, PRODUCT_IMAGES, EXPLORE_IMAGES } from '../config/imagePaths';
import { useComingSoon } from '../contexts/ComingSoonContext';
import { Button, Tag, Icon } from './ui';
import {
  colors, fontFamily, fontSizes, fontWeights, radii, shadows, layout,
} from '../theme/tokens';

const CATEGORIES = [
  { name: 'Textbooks', note: 'Mandatory for every class', img: PRODUCT_IMAGES.TEXTBOOK, to: '/search' },
  { name: 'Notebooks', note: 'Ruled, plain and graph', img: PRODUCT_IMAGES.NOTEBOOK, to: '/search' },
  { name: 'Stationery', note: 'Optional add-ons', img: PRODUCT_IMAGES.STATIONARY, to: '/search' },
  { name: 'Uniforms', note: 'Optional add-ons', img: PRODUCT_IMAGES.UNIFORM, soon: 'Uniforms' },
];

const STEPS = [
  { n: '1', title: 'School Zone', text: 'Search by school name or school code.' },
  { n: '2', title: 'Pick class and section', text: 'See the full book list for your child. Mandatory books are already included.' },
  { n: '3', title: 'Pay and get it delivered', text: 'Add optional items, confirm your address and pay securely online.' },
];

const TRUST = [
  { icon: 'check', text: 'Mandatory books pre-selected' },
  { icon: 'shield', text: 'Secure Razorpay payment' },
  { icon: 'truck', text: 'Track every order' },
];

const HomeScreen = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { open } = useComingSoon();
  const stack = isMobile;

  const gutter = isMobile ? layout.gutterMobile : layout.gutter;
  const sectionTitle = {
    margin: 0,
    fontSize: isMobile ? fontSizes.sectionMobile : fontSizes.section,
    fontWeight: fontWeights.bold,
    color: colors.ink,
  };
  const sectionSub = { margin: 0, fontSize: fontSizes.bodyLg, color: colors.slate };
  const wrap = (padding) => ({
    maxWidth: layout.containerMax,
    margin: '0 auto',
    padding,
    boxSizing: 'border-box',
  });
  const sp = isMobile ? 40 : 72;
  const cardBase = {
    display: 'flex',
    flexDirection: 'column',
    background: colors.white,
    border: '1px solid ' + colors.border,
    overflow: 'hidden',
    minWidth: 0,
    textDecoration: 'none',
    color: 'inherit',
    boxSizing: 'border-box',
  };
  const buttonReset = {
    padding: 0,
    textAlign: 'left',
    fontFamily,
    cursor: 'pointer',
    width: '100%',
  };

  return (
    <div style={{ width: '100%', fontFamily, color: colors.ink, background: colors.white, overflowX: 'hidden' }}>
      {/* Hero */}
      <section style={{ background: colors.mist }}>
        <div
          style={{
            ...wrap(isMobile ? `32px ${gutter}px` : `64px ${gutter}px`),
            display: 'grid',
            gridTemplateColumns: stack ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
            gap: isMobile ? 28 : 48,
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 18 : 24, alignItems: 'flex-start', minWidth: 0 }}>
            <Tag variant="navy" style={{ background: colors.softBlue, padding: '6px 14px', fontSize: fontSizes.small }}>
              Back to school
            </Tag>
            <h1
              style={{
                margin: 0,
                fontSize: isMobile ? fontSizes.displayMobile : isTablet ? 44 : fontSizes.display,
                lineHeight: 1.08,
                fontWeight: fontWeights.bold,
                letterSpacing: -0.5,
                color: colors.ink,
                overflowWrap: 'anywhere',
              }}
            >
              Your child's complete school book set, delivered home.
            </h1>
            <p style={{ margin: 0, fontSize: isMobile ? fontSizes.body : 18, lineHeight: 1.6, color: colors.slate, maxWidth: 520 }}>
              Choose your school, pick the class and section, and order textbooks, notebooks and stationery together in one go.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, width: isMobile ? '100%' : undefined }}>
              <Button to="/search" size="lg" fullWidth={isMobile}>
                School Zone <Icon name="arrowRight" size={18} strokeWidth={2.2} />
              </Button>
              <Button to="/orders" variant="secondary" size="lg" fullWidth={isMobile} style={{ border: '1.5px solid ' + colors.navy }}>
                Track my order
              </Button>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 12 : 24,
                flexWrap: 'wrap',
                marginTop: 12,
                color: colors.slate,
                fontSize: 15,
                fontWeight: fontWeights.medium,
              }}
            >
              {TRUST.map((t) => (
                <span key={t.text} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Icon name={t.icon} size={20} strokeWidth={2} color={colors.teal} />
                  {t.text}
                </span>
              ))}
            </div>
          </div>
          <div
            style={{
              borderRadius: radii.hero,
              overflow: 'hidden',
              background: '#fde9c9',
              border: '1px solid ' + colors.border,
              minWidth: 0,
            }}
          >
            <img fetchPriority="high" decoding="async"
              src={BANNER_IMAGES.HOMEPAGE}
              alt="Illustration of an order being delivered to a doorstep"
              style={{ width: '100%', height: isMobile ? 220 : 400, objectFit: 'cover', display: 'block' }}
            />
          </div>
        </div>
      </section>

      {/* Explore & Shop */}
      <section style={wrap(`${sp}px ${gutter}px 24px`)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
          <h2 style={sectionTitle}>Explore &amp; Shop</h2>
          <p style={sectionSub}>Start with your school, or browse general books.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: stack ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: isMobile ? 16 : 24 }}>
          <Link to="/search" style={{ ...cardBase, borderRadius: 24, boxShadow: shadows.card }}>
            <img loading="lazy" decoding="async" src={EXPLORE_IMAGES.SCHOOL_ZONE} alt="School building with textbooks, notebooks and a school bag" style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }} />
            <div style={{ padding: isMobile ? '20px 20px 24px' : '24px 28px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: isMobile ? 22 : 26, fontWeight: fontWeights.semibold }}>School Zone</h3>
              <p style={{ margin: 0, fontSize: fontSizes.body, lineHeight: 1.6, color: colors.slate }}>
                Find your school and order your class's complete book set in a few steps.
              </p>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: colors.navy, fontSize: fontSizes.body, fontWeight: fontWeights.semibold, marginTop: 6 }}>
                School Zone <Icon name="arrowRight" size={18} strokeWidth={2.2} />
              </span>
            </div>
          </Link>
          <button type="button" onClick={() => open('General Books')} style={{ ...cardBase, ...buttonReset, borderRadius: 24 }}>
            <img loading="lazy" decoding="async" src={EXPLORE_IMAGES.GENERAL_BOOKS} alt="Open storybook with picture books and comics" style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block', opacity: 0.85 }} />
            <div style={{ padding: isMobile ? '20px 20px 24px' : '24px 28px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: isMobile ? 22 : 26, fontWeight: fontWeights.semibold }}>General Books</h3>
                <Tag>Coming soon</Tag>
              </div>
              <p style={{ margin: 0, fontSize: fontSizes.body, lineHeight: 1.6, color: colors.slate }}>
                Story books, comics and general reading, beyond the school list.
              </p>
            </div>
          </button>
        </div>
      </section>

      {/* What's in the set */}
      <section style={wrap(`${isMobile ? 32 : 56}px ${gutter}px 24px`)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
          <h2 style={sectionTitle}>What's in your school's set</h2>
          <p style={sectionSub}>Everything your class needs, grouped the way your school lists it.</p>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
            gap: isMobile ? 12 : 20,
          }}
        >
          {CATEGORIES.map((c) => {
            const inner = (
              <>
                <img loading="lazy" decoding="async" src={c.img} alt={c.name} style={{ width: '100%', height: isMobile ? 110 : 170, objectFit: 'cover', display: 'block' }} />
                <div style={{ padding: isMobile ? '12px 14px 16px' : '16px 18px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: isMobile ? 17 : 19, fontWeight: fontWeights.semibold }}>{c.name}</span>
                  <span style={{ fontSize: fontSizes.small, color: colors.slate }}>{c.note}</span>
                </div>
              </>
            );
            const style = { ...cardBase, background: colors.pageGrey, borderRadius: 20 };
            return c.soon ? (
              <button key={c.name} type="button" onClick={() => open(c.soon)} style={{ ...style, ...buttonReset }}>
                {inner}
              </button>
            ) : (
              <Link key={c.name} to={c.to} style={style}>{inner}</Link>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section style={{ background: colors.pageGrey, marginTop: isMobile ? 32 : 56 }}>
        <div style={wrap(`${isMobile ? 40 : 64}px ${gutter}px`)}>
          <h2 style={{ ...sectionTitle, margin: '0 0 32px' }}>How it works</h2>
          <div style={{ display: 'grid', gridTemplateColumns: stack ? 'minmax(0, 1fr)' : 'repeat(3, minmax(0, 1fr))', gap: isMobile ? 16 : 24 }}>
            {STEPS.map((s) => (
              <div key={s.n} style={{ background: colors.white, border: '1px solid ' + colors.border, borderRadius: 20, padding: isMobile ? 22 : 28, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                <span style={{ width: 44, height: 44, borderRadius: 22, background: colors.navy, color: colors.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: fontWeights.semibold }}>
                  {s.n}
                </span>
                <h3 style={{ margin: 0, fontSize: 22, fontWeight: fontWeights.semibold }}>{s.title}</h3>
                <p style={{ margin: 0, fontSize: fontSizes.body, lineHeight: 1.6, color: colors.slate }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section style={wrap(`${isMobile ? 40 : 64}px ${gutter}px ${isMobile ? 48 : 72}px`)}>
        <h2 style={{ ...sectionTitle, margin: '0 0 28px' }}>Quick Links</h2>
        <div style={{ display: 'grid', gridTemplateColumns: stack ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: isMobile ? 16 : 24 }}>
          <button
            type="button"
            onClick={() => open('Re-order previous')}
            style={{ ...cardBase, ...buttonReset, flexDirection: 'row', alignItems: 'center', gap: 20, padding: isMobile ? '20px' : '24px 28px', borderRadius: 20 }}
          >
            <span style={iconBox}><Icon name="refresh" size={28} /></span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexGrow: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 20, fontWeight: fontWeights.semibold }}>Re-order previous</span>
                <Tag style={{ padding: '3px 10px', fontSize: 12 }}>Coming soon</Tag>
              </div>
              <span style={{ fontSize: 15, color: colors.slate }}>Repeat a past order in a few taps.</span>
            </div>
          </button>
          <Link
            to="/orders"
            style={{ ...cardBase, flexDirection: 'row', alignItems: 'center', gap: 20, padding: isMobile ? '20px' : '24px 28px', borderRadius: 20 }}
          >
            <span style={iconBox}><Icon name="box" size={28} /></span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexGrow: 1, minWidth: 0 }}>
              <span style={{ fontSize: 20, fontWeight: fontWeights.semibold }}>Track my order</span>
              <span style={{ fontSize: 15, color: colors.slate }}>See where your books are right now.</span>
            </div>
            <Icon name="chevronRight" size={20} strokeWidth={2.2} color={colors.navy} />
          </Link>
        </div>
      </section>
    </div>
  );
};

const iconBox = {
  width: 56,
  height: 56,
  borderRadius: 16,
  background: colors.mist,
  color: colors.navy,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export default HomeScreen;
